import { useEffect } from 'react';

/**
 * Хелпер для создания интерфейса параметров учитывая переданный
 * список полей которые будут проверены на наличие значения
 */
type UseSendOnChangedParamsHelper<P, CV extends keyof P> = {
    [K in keyof P]: K extends CV ? P[K] | undefined : P[K];
};

interface UseSendOnChanged {
    (fn: () => void): void;
    <P>(fn: (params: P) => void, params: P): void;
    <P, PK extends keyof P>(
        fn: (params: P) => void,
        params: UseSendOnChangedParamsHelper<P, PK>,
        checkValues: PK[]
    ): void;
}

/**
 * Хук для отправки событий аналитики при изменении одного из передаваемых параметров
 *
 * Если какие-то из обязательных параметров следует проверить на undefined и прежде чем
 * отсылать событие, то и их список можно передать в массиве 3-им аргументом
 */
const useSendOnChanged: UseSendOnChanged = (...args: unknown[]) => {
    const fn = args[0] as (params: unknown) => void;
    const params = args[1] as Record<string, unknown> | undefined;
    const checkValues = args[2] as (keyof typeof params)[] | undefined;
    useEffect(() => {
        if (params && checkValues) {
            for (const value of checkValues) {
                if (params[value] === null || params[value] === undefined) {
                    return;
                }
            }
        }
        fn(params);
        // eslint-disable-next-line react-hooks/exhaustive-deps, @typescript-eslint/no-unsafe-assignment
    }, [...(params ? Object.values(params) : []), ...(checkValues || [])]);
};

export default useSendOnChanged;
